import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useContext, useEffect, useState } from "react";
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { DefaultInput } from "../../../inputs/DefaultInput";
import { bankAccountOptionsFiltered, typeBankAccountOptions, typeBankAccountOptionsLabel } from "../../../../utils/options_selects";
import { APIClient } from "../../../../helpers/api_helper";
import { UPDATE_BANK_ACCOUNT } from "../../../../helpers/url_helper";
import { OfertaViewContext } from "../../../../context/OfertaContext/OfertaViewContext";
import { LoadingView } from "../../../loading/Loading";
import { sanitizarInputNumerico } from "../../../../utils/validateData";

const apiClient = new APIClient();
const timeOut = 2000;

export function EditBank({
    open,
    setOpen,
    accountToEdit,
}) {
    const { updateMyBanks, myBanks } = useContext(OfertaViewContext);
    const [isLoading, setIsLoading] = useState(false);
    const [account, setAccount] = useState(accountToEdit);
    const [bankSelected, setBankSelected] = useState({ label: "Selecciona una opción", value: 0 });
    const [typeAccSelected, setTypeAccSelected] = useState({ label: "Selecciona una opción", value: 0 });
    const [optionsBankAccounts, setOptionsBankAccounts] = useState([]);
    const [optionsTypesAccount, setOptionsTypesAccount] = useState([]);
    const [errorField, setErrorField] = useState({
        bankAccount: "",
        typeBankAccount: "",
        accountNumber: "",
    });
    const [isError, setIsError] = useState(false);
    const [messageError, setMessageError] = useState("Error al agregar la cuenta, intenta luego.");
    const [isSuccess, setIsSuccess] = useState(false);


    const handleChangeBankAccount = (e) => {
        setAccount({
            ...account,
            bankAccount: e.target.value,
        });
    }

    const handleChangeTypeBankAccount = (e) => {
        setAccount({
            ...account,
            typeBankAccount: e.target.value,
        });
    }

    const handleChangeAccountNumber = (e) => {
        setAccount({
            ...account,
            accountNumber: e.target.value,
        });
    };

    const handleClose = () => {
        setOpen(false);
        setIsError(false);
        setIsSuccess(false);
        setMessageError("");
        clearInputs();
    };

    const validateInputs = () => {
        const newErrors = {};
        let hasValidate = true;
        let regexNumber = /^[0-9]+$/;
        if (account.bankAccount === "" || account.bankAccount === "0") {
            newErrors.bankAccount = "Debe seleccionar un banco.";
        } else if (account.typeBankAccount === "" || account.typeBankAccount === "Selecciona una opción") {
            newErrors.typeBankAccount = "Debe seleccionar un tipo de cuenta."
        } else if (account.accountNumber === "") {
            newErrors.accountNumber = "Debe digitar el número de cuenta.";
        } else if (!regexNumber.test(account.accountNumber)) {
            newErrors.accountNumber = "Debe ingresar un número válido.";
        }
        if (Object.keys(newErrors).length > 0) {
            setErrorField(newErrors);
            hasValidate = false;
        } else {
            setErrorField({});
        }
        return hasValidate;
    };

    const clearInputs = () => {
        setErrorField({});
    };

    const editAccount = async () => {
        if (!validateInputs()) return;
        try {
            setIsLoading(true);

            let selected = bankAccountOptionsFiltered.filter((b) => b.label === account?.bankAccount)[0];
            account.accountNumber = sanitizarInputNumerico(account?.accountNumber);
            let payload = {
                bankAccount: String(bankAccountOptionsFiltered.filter((b) => b.label === account?.bankAccount)[0].value),
                typeBankAccount: account?.typeBankAccount,
                accountNumber: account?.accountNumber,
                id: account?.id
            }
            await apiClient.put(UPDATE_BANK_ACCOUNT + "/" + localStorage.getItem('doc'), { update: payload });
            let myBanksData = myBanks;
            let accountsUpdated = myBanksData.myBankAccounts.map((acc) => {
                return acc.id === account?.id ? { ...acc, bankAccount: selected.label, typeBankAccount: account?.typeBankAccount, accountNumber: account?.accountNumber } : acc;
            });
            updateMyBanks({
                ...myBanks,
                myBankAccounts: accountsUpdated,
            });
            setIsSuccess(true);
            setTimeout(() => {
                return handleClose();
            }, timeOut);
        } catch (error) {
            console.error(error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            clearInputs();
        }
    }

    const handleFilterBankToEdit = useCallback(() => {
        let selected = bankAccountOptionsFiltered.filter((b) => b.label === accountToEdit?.bankAccount)[0];
        return selected;
    }, [accountToEdit?.bankAccount]);

    const handleFilterTypesAccountToEdit = useCallback(() => {
        let selected = typeBankAccountOptionsLabel.filter((b) => b.label === accountToEdit?.typeBankAccount)[0];
        return selected;
    }, [accountToEdit?.typeBankAccount]);

    useEffect(() => {
        let modifiedOptionsBanks = bankAccountOptionsFiltered.filter((ba) => ba.label !== accountToEdit?.bankAccount && ba.value !== 0);
        setOptionsBankAccounts(modifiedOptionsBanks);
        setBankSelected(handleFilterBankToEdit());
        let modifiedOptionsTypeAccount = typeBankAccountOptions.filter((type) => type.label !== accountToEdit?.typeBankAccount && type.value !== 0);
        setOptionsTypesAccount(modifiedOptionsTypeAccount);
        setTypeAccSelected(handleFilterTypesAccountToEdit);
    }, [accountToEdit?.bankAccount, accountToEdit?.typeBankAccount, handleFilterBankToEdit, handleFilterTypesAccountToEdit]);

    return (
        <>

            <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-transparent transition-opacity duration-200 ease-in-out data-closed:opacity-0"
                />

                <div className="fixed inset-x-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed md:inset-y-0 bottom-0 md:right-0 flex max-w-full md:pl-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen md:max-w-sm transform h-128 md:h-full transition duration-300 ease-in-out data-closed:translate-x-full"
                            >
                                <div className="flex h-full flex-col rounded-t-3xl md:rounded-none bg-darkBg tracking-tight font-walsheim">
                                    <div>
                                        <div className="py-6">
                                            <div className="flex justify-between px-4 pt-2 md:pt-8">
                                                <div className="flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        onClick={handleClose}
                                                        className="relative text-white focus:outline-hidden"
                                                    >
                                                        <span className="absolute" />
                                                        <span className="sr-only">Close panel</span>
                                                        <ArrowLeftIcon
                                                            aria-hidden="true"
                                                            className="h-6 w-6"
                                                        />
                                                    </button>
                                                </div>
                                                <DialogTitle>
                                                    <div className="text-white">
                                                        Editar cuenta bancaria
                                                    </div>
                                                </DialogTitle>
                                                <div className="flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        onClick={handleClose}
                                                        className="relative text-white focus:outline-hidden"
                                                    >
                                                        <span className="absolute" />
                                                        <span className="sr-only">Close panel</span>
                                                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="pt-2 px-4 text-sm text-grayP">
                                                Recuerda que debe estar a tu nombre para que el
                                                desembolso sea exitoso
                                            </div>
                                        </div>
                                    </div>
                                    <div className="h-full grid content-between p-4 rounded-t-3xl bg-white overflow-auto">
                                        <div className="lg:p-4 pt-4">
                                            <div>
                                                <h2 className="font-bold">Banco</h2>
                                                {optionsBankAccounts && (
                                                    <select
                                                        required
                                                        onChange={(choice) => handleChangeBankAccount(choice)}
                                                        id="bankAccount"
                                                        name="bankAccount"
                                                        className="py-2 px-4 block w-full border-0 border-b focus:border-purple focus:ring-0 sm:text-base"
                                                    >
                                                        <option value={bankSelected?.value}>{bankSelected?.label}</option>
                                                        {optionsBankAccounts.map((item, index) => (
                                                            <option value={item.value} key={index}>
                                                                {item.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                {
                                                    errorField?.bankAccount && (
                                                        <span className="text-red">{errorField?.bankAccount}</span>
                                                    )
                                                }
                                            </div>
                                            <div className="pt-8">
                                                <h2 className="font-bold">Tipo de cuenta</h2>
                                                {optionsTypesAccount && (
                                                    <select
                                                        required
                                                        onChange={(choice) => handleChangeTypeBankAccount(choice)}
                                                        id="typeBankAccount"
                                                        name="typeBankAccount"
                                                        className="py-2 px-4 block w-full border-0 border-b focus:border-purple focus:ring-0"
                                                    >
                                                        <option value={typeAccSelected?.value}>{typeAccSelected?.label}</option>
                                                        {optionsTypesAccount.map((item, index) => (
                                                            <option key={index}>{item.label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {
                                                    errorField?.typeBankAccount && (
                                                        <span className="text-red">{errorField?.typeBankAccount}</span>
                                                    )
                                                }

                                            </div>
                                            <div className="pt-8">
                                                <h2 className="font-bold">Número de cuenta</h2>
                                                <DefaultInput
                                                    onChange={handleChangeAccountNumber}
                                                    value={account.accountNumber}
                                                    type='number'
                                                />
                                                {
                                                    errorField?.accountNumber && (
                                                        <span className="text-red">{errorField?.accountNumber}</span>
                                                    )
                                                }
                                            </div>
                                        </div>

                                        <div>
                                            {
                                                isError && (
                                                    <>
                                                        <div className="flex justify-center py-2">
                                                            {" "}
                                                            <span className="text-sm text-red pt-2">
                                                                {messageError}
                                                            </span>{" "}
                                                        </div>
                                                        <div className="flex justify-center text-red py-2">
                                                            <XCircleIcon className="w-20 h-auto" />
                                                        </div>
                                                    </>
                                                )
                                            }
                                            {
                                                isSuccess && (
                                                    <>
                                                        <div className="flex justify-center py-2">
                                                            {" "}
                                                            <span className="text-sm text-green pt-2">
                                                                Cuenta actualizada con éxito
                                                            </span>{" "}
                                                        </div>
                                                        <div className="flex justify-center text-green py-2">
                                                            <CheckCircleIcon className="w-20 h-auto" />
                                                        </div>
                                                    </>
                                                )
                                            }
                                            {
                                                isLoading && (
                                                    <LoadingView text={"Agregando cuenta..."} />
                                                )
                                            }
                                            <button
                                                disabled={isLoading || isError || isSuccess}
                                                onClick={editAccount}
                                                type="button"
                                                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-xs text-lg text-white font-semibold ${isLoading || isError || isSuccess ? "bg-tgray" : "bg-purple hover:bg-purpleDark hover:scale-100 transition duration-300  focus:outline-hidden"}`}
                                            >
                                                <div className="flex">
                                                    Actualizar cuenta
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
